/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-floating-promises */
import { OnChangeFn, SortingState } from "@tanstack/react-table";
import { capitalize } from "lodash";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  useComputedLabels, useViews, useWorkspaceMembers, useWorkspaceObjectSchema,
} from "../../../hooks/QueryHooks";
import type { ComputedLabel } from "../../../services/computedTraits/computedTraitsEndpoints.interface";
import { isMetricLikeField, isUnsupportedObjectField } from "../../../shared/table/objectSearch";
import { LegacyColumn } from "../../../shared/table/legacyTanStackAdapter";
import TanStackTable from "../../../shared/table/TanStackTable";
import useObjectSearchTable from "../../../shared/table/useObjectSearchTable";
import useViewStore from "../../../store/view/viewStore";
import useWorkspaceStore from "../../../store/workspace/workspaceStore";
import parseTableData from "../../../utils/parseTable";
import { AccountControls } from "./components";
import AccountsKanbanView from "./components/AccountsKanbanView";

const AccountsPage = () => {
  const { viewId } = useParams<{ viewId: string }>();
  const location = useLocation();
  const { updateView, setView, currentView }: any = useViewStore((state: any) => ({
    updateView: state.updateView,
    setView: state.setView,
    currentView: state.view,
  }));

  const workspace = useWorkspaceStore((state) => state.workspace);

  const { data: workspaceMembers, isLoading: isMembersLoading }: any = useWorkspaceMembers({
    workspaceId: workspace?.id,
    enabled: !!workspace?.id,
    retry: 2,
  });

  const workspaceSchema: any = useWorkspaceObjectSchema({
    workspaceId: workspace?.id,
    enabled: !!workspace?.id,
  });

  const views: any = useViews({
    workspaceId: workspace?.id,
    enabled: !!workspace?.id,
  });

  const computedLabelsQuery: any = useComputedLabels({
    workspaceId: workspace?.id,
    enabled: !!workspace?.id,
  });

  useEffect(() => {
    if (viewId && views.isSuccess && views?.data?.length && !location.pathname.includes("new")) {
      setView(views?.data?.find((view: any) => view.id === viewId));
    }
  }, [viewId, views.isSuccess]);

  const entitySlug = currentView?.entity_relation === "accounts" ? "accounts" : "users";
  const traits: any = useMemo(() => {
    const baseTraits = workspaceSchema?.data?.fieldsByObjectSlug?.[entitySlug] || { list: [], obj: {} };
    const scopedMetricTraits = workspaceSchema?.data?.metricsByObjectSlug?.[entitySlug]?.list || [];
    const globalMetricTraits = (workspaceSchema?.data?.metrics?.list || []).filter((item: any) => (
      !item?.object_slug || item?.object_slug === entitySlug
    ));
    const metricTraitsList = scopedMetricTraits.length ? scopedMetricTraits : globalMetricTraits;
    const mergedList = [...(baseTraits?.list || []), ...metricTraitsList];
    const mergedObj = mergedList.reduce((acc: Record<string, any>, item: any) => {
      if (item?.name) acc[item.name] = item;
      return acc;
    }, {});
    return {
      list: mergedList,
      obj: mergedObj,
    };
  }, [workspaceSchema?.data?.fieldsByObjectSlug, workspaceSchema?.data?.metricsByObjectSlug, workspaceSchema?.data?.metrics?.list, entitySlug]);

  // Index computed-label definitions (for the current entity) by both their bare `name`
  // (e.g. "lifecycle_stage") and their DB `column_name` (e.g. "c__lifecycle_stage").
  const labelDefsByKey = useMemo(() => {
    const map: Record<string, ComputedLabel> = {};
    ((computedLabelsQuery?.data || []) as ComputedLabel[])
      .filter((label) => label.entity_type === entitySlug)
      .forEach((label) => {
        if (label.name) map[label.name] = label;
        if (label.column_name) map[label.column_name] = label;
      });
    return map;
  }, [computedLabelsQuery?.data, entitySlug]);

  // Resolve a column/trait key to a matching label definition. Handles the entity prefix
  // (accounts./users.) and the `c__` computed-column prefix: a field keyed on
  // "accounts.lifecycle_stage", "lifecycle_stage", or "accounts.c__lifecycle_stage" all
  // resolve to the label whose bare name is "lifecycle_stage".
  const resolveLabelDef = useCallback((key: string): ComputedLabel | null => {
    if (!key) return null;
    const bare = key.includes(".") ? key.split(".").slice(1).join(".") : key;
    const withoutCPrefix = bare.startsWith("c__") ? bare.slice(3) : bare;
    return labelDefsByKey[bare]
      || labelDefsByKey[withoutCPrefix]
      || labelDefsByKey[`c__${withoutCPrefix}`]
      || null;
  }, [labelDefsByKey]);

  const accountControlsRef = useRef<any>(null);
  const [tableHeight, setTableHeight] = useState<number>((window.innerHeight - 320) > 300 ? (window.innerHeight - 320) : 300);

  const updateTableHeight = useCallback(() => {
    const controlsHeight = accountControlsRef.current?.offsetHeight || 0;
    const headerOffset = 130;
    const dynamicHeight = (window.innerHeight - headerOffset - controlsHeight) > 400
      ? (window.innerHeight - headerOffset - controlsHeight)
      : 400;
    setTableHeight(dynamicHeight);
  }, []);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      updateTableHeight();
    });

    if (accountControlsRef.current) {
      resizeObserver.observe(accountControlsRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [traits]);

  useEffect(() => {
    const handleResize = () => updateTableHeight();
    window.addEventListener("resize", handleResize);
    updateTableHeight();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isUserEntity = currentView?.entity_relation !== "accounts";
  const entityPrefix = isUserEntity ? "users" : "accounts";
  const fallbackSortField = isUserEntity ? "users.last_seen" : "accounts.last_seen";

  const selectedNonMetricFields = useMemo(
    () => (currentView?.selected_columns || [])
      .filter((field: string) => !isMetricLikeField(field))
      .filter((field: string) => !isUnsupportedObjectField(field)),
    [currentView?.selected_columns],
  );

  const requiredFields = useMemo(
    () => (isUserEntity
      ? ["users.name", "users.external_id", "users.email", "users.avatar_url"]
      : ["accounts.name", "accounts.external_id", "accounts.avatar_url"]),
    [isUserEntity],
  );

  const legacyColumns = useMemo<LegacyColumn[]>(() => {
    if (!traits?.obj) return [];
    const sourceFields = [...selectedNonMetricFields, "related_accounts"];

    return sourceFields.reduce((columns: LegacyColumn[], item: string) => {
      if (!traits.obj[item] || traits.obj[item]?.exclude_display) {
        if (item === "related_accounts" && isUserEntity) {
          columns.push({
            key: "related_accounts",
            title: "Related Accounts",
            dataType: "object",
            isSortable: true,
            attributeType: "trait",
          });
        }
        return columns;
      }

      const definition = traits.obj[item];
      const nextColumn: LegacyColumn = {
        key: definition.name,
        dataType: definition.data_type,
        title: definition.display_name,
        isSortable: true,
        attributeType: definition.attribute_type,
        displayFormat: definition.display_format || null,
        generationType: definition.generation_type || null,
        sortType: currentView?.sort_fields?.field === item ? "property" : undefined,
      };

      const labelDef = resolveLabelDef(definition.name) || resolveLabelDef(item);
      if (labelDef && labelDef.label_options?.length) {
        nextColumn.isLabelField = true;
        nextColumn.labelOptions = labelDef.label_options;
      }

      if (item === "accounts.name" || item === "users.name") {
        columns.unshift(nextColumn);
      } else {
        columns.push(nextColumn);
      }
      return columns;
    }, []);
  }, [traits?.obj, selectedNonMetricFields, currentView?.sort_fields?.field, isUserEntity, resolveLabelDef]);

  const {
    rows,
    total,
    pageCount,
    pageIndex,
    pageSize,
    sorting,
    setSort: setTableSort,
    setPageIndex,
    isLoading,
    isFetching,
  } = useObjectSearchTable({
    workspaceId: workspace?.id,
    entityType: currentView?.entity_relation,
    enabled: !!workspace?.id && !!currentView?.id && !!traits?.obj,
    initialPageSize: 15,
    initialSort: {
      field: !isMetricLikeField(currentView?.sort_fields?.field)
        ? (currentView?.sort_fields?.field || fallbackSortField)
        : fallbackSortField,
      order: currentView?.sort_fields?.order || "desc",
    },
    queryOptions: {
      filter: currentView?.filters,
      selectedFields: selectedNonMetricFields,
      requiredFields,
      fallbackSortField,
      includeRelatedAccountsForUsers: isUserEntity,
      relatedAccountsLimit: 10,
    },
    parseRows: (rawRows) => {
      const mappedRows = (rawRows || []).map((record: any) => {
        const row: any = {};
        const fields = record?.fields || {};

        Object.entries(fields).forEach(([key, value]) => {
          row[`${entityPrefix}.${key}`] = value;
        });

        if (!row[`${entityPrefix}.external_id`] && record?.id) {
          row[`${entityPrefix}.external_id`] = record.id;
        }

        if (isUserEntity) {
          const relatedAccounts = record?.related?.accounts || [];
          row.related_accounts = relatedAccounts.map((account: any) => ({
            "accounts.name": account?.name,
            "accounts.external_id": account?.id || account?.external_id,
            "accounts.avatar_url": account?.avatar_url,
          }));
        }

        return row;
      });

      return parseTableData({ data: mappedRows, memberData: workspaceMembers?.users });
    },
    resetPageDeps: [
      currentView?.id,
      JSON.stringify(selectedNonMetricFields || []),
      JSON.stringify(currentView?.filters || {}),
      currentView?.sort_fields?.field,
      currentView?.sort_fields?.order,
      currentView?.entity_relation,
    ],
  });

  const isPageLoading = isMembersLoading || isLoading || isFetching || !traits?.obj;
  const displayMode = useMemo(
    () => (["kanban", "board", "cards"].includes(currentView?.display_mode) ? "kanban" : "list"),
    [currentView?.display_mode],
  );

  useEffect(() => {
    document.title = `${currentView?.view_name ? `${capitalize(currentView?.view_name)} | ` : ""}View | Hyperengage`;
  }, [currentView?.view_name]);

  useEffect(() => {
    if (!currentView?.sort_fields?.field) return;

    const nextSortField = !isMetricLikeField(currentView?.sort_fields?.field)
      ? currentView.sort_fields.field
      : fallbackSortField;

    setTableSort({
      field: nextSortField,
      order: currentView.sort_fields.order || "desc",
    });
  }, [currentView?.sort_fields?.field, currentView?.sort_fields?.order, fallbackSortField, setTableSort]);

  const onTableSortingChange: OnChangeFn<SortingState> = useCallback((updater) => {
    const nextSorting = typeof updater === "function" ? updater(sorting) : updater;
    const nextSort = (nextSorting || []).slice(0, 1)?.[0];
    if (!nextSort?.id) return;

    const nextField = nextSort.id;
    const nextOrder = nextSort.desc ? "desc" : "asc";

    updateView({
      sort_fields: {
        field: nextField,
        order: nextOrder,
        type: "property",
      },
    });
    setTableSort({
      field: nextField,
      order: nextOrder,
    });
  }, [sorting, updateView, setTableSort]);

  return (
    <div className="relative flex h-full flex-col bg-background">
      {traits && (
        <div ref={accountControlsRef}>
          <AccountControls
            traits={traits}
            currentView={currentView}
            views={views?.data || []}
          />
        </div>
      )}
      {isPageLoading && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-background/60 backdrop-blur-[1px]">
          <span className="h-6 w-6 animate-spin rounded-full border-2 border-primary/30 border-t-primary" />
        </div>
      )}
      <div className="flex-1 px-6 pb-6 pt-4">
        {displayMode === "kanban" ? (
          <AccountsKanbanView
            rows={rows}
            traits={traits}
            selectedColumns={selectedNonMetricFields}
            entityRelation={currentView?.entity_relation}
            total={total}
            pageIndex={pageIndex}
            pageSize={pageSize}
            pageCount={pageCount}
            onPageIndexChange={setPageIndex}
          />
        ) : (
          <TanStackTable
            legacyColumns={legacyColumns}
            data={rows}
            totalData={total}
            pageIndex={pageIndex}
            pageSize={pageSize}
            pageCount={pageCount}
            onPageIndexChange={setPageIndex}
            sorting={sorting}
            onSortingChange={onTableSortingChange}
            loading={isLoading || isFetching}
            noDataTitle={currentView?.entity_relation === "accounts" ? "No Accounts Available" : "No Contacts Available"}
            noDataMessage="We couldn't find any data for the given filters"
            itemType={currentView?.entity_relation === "accounts" ? "Accounts" : "Contacts"}
            height={tableHeight}
            enableColumnReorder
            enableColumnPinning
            surfaceVariant="lovable"
          />
        )}
      </div>
    </div>
  );
};

export default AccountsPage;
